import { useQuery } from '@apollo/client'
import {
    Autocomplete,
    Box,
    Checkbox,
    FormControlLabel,
    TextField,
} from '@mui/material'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { GetOfficeLocationsByName } from './OfficeLocationsAutocomplete.gql'
import { OfficeLocationsAutocompleteProps } from './OfficeLocationsAutocompleteProps'

const LIMIT = 100

export const LOCATION_SENTINEL_INTERNATIONAL = '__INTERNATIONAL__'
export const LOCATION_SENTINEL_UNITED_STATES = '__UNITED_STATES__'
export const LOCATION_SENTINEL_TRI_STATE = '__TRI_STATE_AREA__'

const SENTINEL_OPTIONS = [
    { id: LOCATION_SENTINEL_INTERNATIONAL, value: 'International' },
    { id: LOCATION_SENTINEL_UNITED_STATES, value: 'United States' },
    { id: LOCATION_SENTINEL_TRI_STATE, value: 'Tri-State Area' },
]

/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param OfficeLocationsAutocompleteProps
 * @returns MUI Autocomplete with paginated options (multi-select)
 */
const OfficeLocationsAutocompleteMultiple: FC<
    OfficeLocationsAutocompleteProps<true>
> = ({
    error,
    helperText,
    size,
    placeholder,
    label = 'Office Locations',
    enableStateCountryCheckbox = false,
    includeSentinels = false,
    includeStateCountry: includeStateCountryProp,
    onIncludeStateCountryChange,
    searchStateOnly = false,
    transformOptions,
    renderOption: customRenderOption,
    onChange,
    value,
    ...props
}) => {
    const [name, setName] = useState('')
    const [debounceName, setDebouncedName] = useState(name)
    const [open, setOpen] = useState(false)

    // Internal state for the checkbox (used when uncontrolled)
    const [includeStateCountryInternal, setIncludeStateCountryInternal] =
        useState(false)

    // Use controlled value if provided, otherwise use internal state
    const includeStateCountry =
        includeStateCountryProp ?? includeStateCountryInternal

    // Log deprecation warning for searchStateOnly
    useEffect(() => {
        if (searchStateOnly) {
            console.warn(
                '[OfficeLocationsAutocompleteMultiple] searchStateOnly is deprecated. Use enableStateCountryCheckbox instead.'
            )
        }
    }, [searchStateOnly])

    // Use parent's open state if provided, otherwise use internal state
    const effectiveOpen = props.open ?? open

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(name)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    // Transform search term if searching state/country only (deprecated behavior)
    const searchTerm =
        searchStateOnly && debounceName ? `, %${debounceName}` : debounceName

    const { data, fetchMore, loading } = useQuery(GetOfficeLocationsByName, {
        variables: { name: searchTerm, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: !effectiveOpen,
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: searchTerm,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.officeLocations?.data ||
                    !fetchMoreResult?.officeLocations?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedCompaniesData = Array.from(
                    previousData.officeLocations.data
                )
                const pageLimit =
                    previousData.officeLocations.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.officeLocations.data.length;
                    i++
                ) {
                    updatedCompaniesData[(page - 1) * limit + i] =
                        fetchMoreResult.officeLocations.data[i]
                }
                const newCache = {
                    ...previousData,
                    officeLocations: {
                        ...fetchMoreResult.officeLocations,
                        data: updatedCompaniesData,
                    },
                }
                if (
                    fetchMoreResult?.officeLocations?.pageInfo.itemTotal &&
                    fetchMoreResult.officeLocations.pageInfo.itemTotal ===
                        updatedCompaniesData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, searchTerm])

    /**
     * Handles checkbox toggle. Calls the callback if provided, otherwise updates internal state.
     */
    const handleCheckboxChange = useCallback(
        (checked: boolean) => {
            if (onIncludeStateCountryChange) {
                onIncludeStateCountryChange(checked)
            } else {
                setIncludeStateCountryInternal(checked)
            }
        },
        [onIncludeStateCountryChange]
    )

    const observerCallback = useCallback(
        (entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        },
        [loadMoreData]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
        // effectiveOpen is a dependency because we lose the observedElementRef when the dropdown closes and need to re-observe it once we open again
    }, [observerCallback, effectiveOpen, data?.officeLocations.data])

    // Deduplicate locations by value, keeping first occurrence
    const allLocations = data?.officeLocations.data || []
    const deduplicatedLocations = allLocations.reduce(
        (acc, location) => {
            if (!acc.some((l) => l.value === location.value)) {
                acc.push(location)
            }
            return acc
        },
        [] as typeof allLocations
    )

    // If search term exactly matches a group (state/country after comma), bump that group to top
    const searchTrimmed = debounceName.trim().toLowerCase()
    const sortedLocations = searchTrimmed
        ? [...deduplicatedLocations].sort((a, b) => {
              const aGroup = a.value
                  .split(', ')
                  .slice(1)
                  .join(', ')
                  .toLowerCase()
              const bGroup = b.value
                  .split(', ')
                  .slice(1)
                  .join(', ')
                  .toLowerCase()
              const aExactMatch = aGroup === searchTrimmed
              const bExactMatch = bGroup === searchTrimmed
              if (aExactMatch && !bExactMatch) return -1
              if (!aExactMatch && bExactMatch) return 1
              return 0
          })
        : deduplicatedLocations

    const optionsWithSentinels = includeSentinels
        ? [...SENTINEL_OPTIONS, ...sortedLocations]
        : sortedLocations

    const transformedOptions = transformOptions
        ? transformOptions(optionsWithSentinels)
        : optionsWithSentinels

    // Include current value items in options to prevent MUI warnings when dropdown is closed
    // (query is skipped when closed, so options would be empty without this)
    const valueItems = value || []
    const finalOptions = [
        ...transformedOptions,
        ...valueItems.filter(
            (v) =>
                !transformedOptions.some((opt) => opt.value === v.value) &&
                !SENTINEL_OPTIONS.some((opt) => opt.id === v.id)
        ),
    ]

    return (
        <Box>
            <Autocomplete
                fullWidth
                multiple
                filterOptions={(x) => x}
                disablePortal
                getOptionLabel={(option) => option.value}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (
                        reason !== 'selectOption' &&
                        reason !== 'removeOption'
                    ) {
                        setOpen(false)
                    }
                }}
                open={open}
                loading={loading}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                renderOption={(liProps, option, state, ownerState) => {
                    const isObserver =
                        state.index === ownerState.options.length - 1

                    if (customRenderOption) {
                        const element = customRenderOption(
                            liProps,
                            option,
                            state,
                            ownerState
                        )
                        if (isObserver && React.isValidElement(element)) {
                            return React.cloneElement(element, {
                                ref: observedElementRef,
                            } as React.Attributes & {
                                ref: typeof observedElementRef
                            })
                        }
                        return element
                    }

                    return (
                        <li
                            {...liProps}
                            key={option.id}
                            ref={isObserver ? observedElementRef : null}
                        >
                            {option.value}
                        </li>
                    )
                }}
                onInputChange={(e, inputValue) => {
                    setName(inputValue)
                }}
                {...props}
                value={value}
                onChange={onChange}
                options={finalOptions}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size={size}
                        label={label}
                        variant="outlined"
                        error={error}
                        helperText={helperText}
                        placeholder={placeholder}
                    />
                )}
            />
            {enableStateCountryCheckbox && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeStateCountry}
                            onChange={(e) =>
                                handleCheckboxChange(e.target.checked)
                            }
                            disabled={!value || value.length === 0}
                            size="small"
                            sx={{ py: 0, px: 0.5 }}
                        />
                    }
                    label="Include all locations in state/country"
                    slotProps={{ typography: { variant: 'caption' } }}
                    sx={{ px: 1, mt: 0.5 }}
                />
            )}
        </Box>
    )
}

export default OfficeLocationsAutocompleteMultiple
